import { Payload, PayloadCategory } from "@/types/profile";
import { LucideIcon } from "lucide-react";
import * as Icons from "lucide-react";

// Map payload categories to appropriate icons
const categoryIconMap: Record<PayloadCategory, string> = {
  Security: "Shield",
  Network: "Wifi",
  General: "Settings",
  Accounts: "Users",
  Restrictions: "Ban",
  Media: "Play",
  Applications: "AppWindow",
  Advanced: "Wrench",
  Education: "GraduationCap"
};

// Special payload-specific icons for more granular matching
const payloadIconMap: Record<string, string> = {
  "passcode": "Lock",
  "firewall": "ShieldAlert",
  "wifi": "Wifi",
  "vpn": "ShieldCheck",
  "certificate": "FileKey",
  "encryption": "Key",
  "privacy-preferences": "Eye",
  "login-window": "LogIn",
  "restrictions": "Ban",
  "single-app-mode": "Smartphone",
  "airplay": "Cast",
  "airprint": "Printer",
  "app-install": "Download",
};

/**
 * Determines the best icon for a profile based on its payloads
 * Priority order:
 * 1. Most specific payload icon if available
 * 2. Most common category icon
 * 3. Default "Settings" icon
 */
export function selectProfileIcon(payloads: Payload[]): string {
  if (payloads.length === 0) {
    return "Settings";
  }

  // First, check if any payload has a specific icon mapping
  for (const payload of payloads) {
    if (payloadIconMap[payload.id]) {
      return payloadIconMap[payload.id];
    }
  }

  // Count categories to find the most common one
  const categoryCounts: Record<string, number> = {};
  payloads.forEach(payload => {
    categoryCounts[payload.category] = (categoryCounts[payload.category] || 0) + 1;
  });

  // Find the most common category
  const dominantCategory = Object.entries(categoryCounts)
    .sort(([, a], [, b]) => b - a)[0]?.[0] as PayloadCategory;

  return categoryIconMap[dominantCategory] || "Settings";
}

/**
 * Get the Lucide icon component from icon name string
 */
export function getIconComponent(iconName: string): LucideIcon {
  const IconComponent = (Icons as any)[iconName];
  return IconComponent || Icons.Settings;
}

/**
 * Get a human-readable description of what the icon represents
 */
export function getIconDescription(iconName: string): string {
  const descriptions: Record<string, string> = {
    Shield: "Security configuration",
    Lock: "Access control",
    ShieldAlert: "Security protection",
    ShieldCheck: "VPN & secure connections",
    Wifi: "Network settings",
    Settings: "General configuration",
    Users: "User accounts",
    Ban: "Restrictions & policies",
    Play: "Media settings",
    AppWindow: "Application management",
    Wrench: "Advanced settings",
    GraduationCap: "Education settings",
    FileKey: "Certificate management",
    Key: "Encryption settings",
    Eye: "Privacy settings",
    LogIn: "Login & authentication",
    Smartphone: "Device restrictions",
    Cast: "Media streaming",
    Printer: "Printing settings",
    Download: "App deployment"
  };
  return descriptions[iconName] || "Configuration profile";
}
